import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiStar, FiHeart, FiZap, FiSmile, FiTool } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiShield,
      title: 'General Dentistry',
      description: 'Comprehensive dental care including cleanings, fillings, and preventive treatments.',
      features: ['Regular Cleanings', 'Dental Fillings', 'Root Canal Therapy', 'Tooth Extractions', 'Gum Disease Treatment'],
      image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=400&h=300&fit=crop'
    },
    {
      icon: FiStar,
      title: 'Cosmetic Dentistry',
      description: 'Transform your smile with our aesthetic dental treatments and procedures.',
      features: ['Teeth Whitening', 'Porcelain Veneers', 'Dental Bonding', 'Smile Makeovers', 'Gum Contouring'],
      image: 'https://images.unsplash.com/photo-1609840114035-3c981960afdd?w=400&h=300&fit=crop'
    },
    {
      icon: FiHeart,
      title: 'Orthodontics',
      description: 'Straighten your teeth and improve your bite with modern orthodontic solutions.',
      features: ['Traditional Braces', 'Invisalign', 'Clear Aligners', 'Retainers', 'Bite Correction'],
      image: 'https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=400&h=300&fit=crop'
    },
    {
      icon: FiZap,
      title: 'Emergency Dental Care',
      description: '24/7 emergency dental services for urgent dental problems and pain relief.',
      features: ['Severe Toothache', 'Dental Trauma', 'Lost Fillings', 'Broken Teeth', 'Urgent Repairs'],
      image: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=400&h=300&fit=crop'
    },
    {
      icon: FiSmile,
      title: 'Pediatric Dentistry',
      description: 'Specialized dental care for children in a fun and comfortable environment.',
      features: ['Child-Friendly Approach', 'Preventive Care', 'Fluoride Treatments', 'Dental Sealants', 'Early Orthodontics'],
      image: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=400&h=300&fit=crop'
    },
    {
      icon: FiTool,
      title: 'Oral Surgery',
      description: 'Advanced surgical procedures performed with precision and care.',
      features: ['Wisdom Tooth Removal', 'Dental Implants', 'Bone Grafting', 'Oral Pathology', 'TMJ Treatment'],
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=300&fit=crop'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dental Services - SmileCare Dental | Comprehensive Dental Care</title>
        <meta name="description" content="Comprehensive dental services including general dentistry, cosmetic treatments, orthodontics, emergency care, pediatric dentistry & oral surgery. Expert care for all ages." />
        <meta name="keywords" content="dental services, general dentistry, cosmetic dentistry, orthodontics, emergency dental care, pediatric dentistry, oral surgery" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-blue-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Our Dental Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Comprehensive dental care services designed to keep your smile healthy, beautiful, and functional for life.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mr-4">
                      <SafeIcon icon={service.icon} className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Services Include:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-600">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Treatment Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a systematic approach to ensure you receive the best possible care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Consultation',
                description: 'Comprehensive examination and discussion of your dental concerns and goals.'
              },
              {
                step: '02',
                title: 'Diagnosis',
                description: 'Detailed analysis using advanced technology to identify issues and create treatment plans.'
              },
              {
                step: '03',
                title: 'Treatment',
                description: 'Professional treatment delivered with precision, comfort, and care.'
              },
              {
                step: '04',
                title: 'Follow-up',
                description: 'Ongoing care and maintenance to ensure lasting results and optimal oral health.'
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 rounded-lg p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Insurance & Financing
                </h2>
                <p className="text-gray-600 mb-6">
                  We accept most major insurance plans and offer flexible financing options to make quality dental care accessible to everyone.
                </p>
                <div className="space-y-3">
                  {[
                    'Most major insurance plans accepted',
                    'Flexible payment plans available',
                    'CareCredit financing options',
                    'Free insurance verification',
                    'Transparent pricing with no surprises'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-center">
                <img
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop"
                  alt="Insurance and Financing"
                  className="rounded-lg shadow-lg mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;